import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { countriesQuery, type Country } from '@/data';
import { scoreCountries, type CountryMatchResult } from '@/features/quiz';
import { MARKETING_PRESETS } from '../presets';
import { uiLang } from '@/i18n/lang';

const CYCLE_MS = 4500;

interface Slot {
  countryCode: string;
  match: CountryMatchResult;
  country: Country | undefined;
}

/**
 * Precompute the top-5 for all 6 presets in one pass. We previously
 * recomputed on every preset rotation (every 4.5s) which scored 30 countries
 * × 6 times over the cycle — wasted work since presets + countries are
 * immutable once data loads.
 */
function computeAllPresetTops(
  countries: Country[],
  byCode: Map<string, Country>,
): Slot[][] {
  return MARKETING_PRESETS.map((preset) => {
    const result = scoreCountries(countries, preset.answers, 5);
    return result.topMatches.map((m) => ({
      countryCode: m.countryCode,
      match: m,
      country: byCode.get(m.countryCode),
    }));
  });
}

/**
 * Hero-right card: cycles through MARKETING_PRESETS and shows top-5 ranking
 * for each. Pauses on hover/focus, respects prefers-reduced-motion.
 */
export function LiveRankingPreview() {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n.language);
  const { data: file } = useQuery(countriesQuery());

  const [presetIdx, setPresetIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [cycleResetKey, setCycleResetKey] = useState(0);
  const prefersReducedMotion = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mq.matches;
    const update = () => (prefersReducedMotion.current = mq.matches);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (paused || prefersReducedMotion.current) return;
    const id = window.setInterval(() => {
      setPresetIdx((i) => (i + 1) % MARKETING_PRESETS.length);
    }, CYCLE_MS);
    return () => window.clearInterval(id);
  }, [paused, cycleResetKey]);

  function goTo(i: number) {
    setPresetIdx(i);
    setCycleResetKey((k) => k + 1);
  }

  function goPrev() {
    goTo((presetIdx - 1 + MARKETING_PRESETS.length) % MARKETING_PRESETS.length);
  }

  function goNext() {
    goTo((presetIdx + 1) % MARKETING_PRESETS.length);
  }

  const byCode = useMemo<Map<string, Country>>(
    () => new Map((file?.countries ?? []).map((c) => [c.code, c])),
    [file],
  );

  // Precomputed once per countries load (not per preset rotation).
  const allPresetTops = useMemo<Slot[][]>(
    () => (file ? computeAllPresetTops(file.countries, byCode) : []),
    [file, byCode],
  );

  const slots = allPresetTops[presetIdx] ?? [];

  const presetLabel = t(MARKETING_PRESETS[presetIdx].labelKey);

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      tabIndex={-1}
      className="relative rounded-xl border border-ink-100 bg-paper-2/70 p-5 shadow-sm backdrop-blur-sm sm:p-6"
    >
      <p className="flex items-center justify-between gap-3 font-mono text-[11px] uppercase tracking-wider text-ink-500">
        <span>{t('home.hero.preview.label')}</span>
        <span className="flex items-center gap-1.5">
          <button
            onClick={goPrev}
            aria-label="Previous preset"
            className="flex h-4 w-4 items-center justify-center rounded text-ink-400 hover:text-ink-700 transition-colors"
          >
            ‹
          </button>
          {MARKETING_PRESETS.map((p, i) => (
            <button
              key={p.id}
              onClick={() => goTo(i)}
              aria-label={t(p.labelKey)}
              aria-current={i === presetIdx ? 'true' : undefined}
              className={`h-1.5 rounded-full transition-all ${
                i === presetIdx ? 'w-4 bg-terra-600' : 'w-2 bg-ink-200 hover:bg-ink-400'
              }`}
            />
          ))}
          <button
            onClick={goNext}
            aria-label="Next preset"
            className="flex h-4 w-4 items-center justify-center rounded text-ink-400 hover:text-ink-700 transition-colors"
          >
            ›
          </button>
        </span>
      </p>

      <ul
        aria-live="polite"
        aria-label={presetLabel}
        className="mt-4 space-y-2"
      >
        {slots.length === 0
          ? Array.from({ length: 5 }).map((_, i) => (
              <li
                key={`skel-${i}`}
                className="flex items-center gap-3 rounded-md bg-paper px-3 py-2 opacity-50"
                aria-hidden="true"
              >
                <span className="w-5 font-mono text-xs text-ink-300">#{i + 1}</span>
                <span className="h-4 w-4 rounded-full bg-ink-100" />
                <span className="h-3 flex-1 rounded bg-ink-100" />
                <span className="h-3 w-10 rounded bg-ink-100" />
              </li>
            ))
          : slots.map((slot, i) => {
              const percent = Math.round(slot.match.score * 100);
              const name = slot.country
                ? lang === 'ru'
                  ? slot.country.name_ru
                  : slot.country.name_en
                : slot.countryCode;
              return (
                <li
                  key={slot.countryCode}
                  data-testid={`live-rank-${i + 1}`}
                  className="flex items-center gap-3 rounded-md bg-paper px-3 py-2 transition-all duration-300 motion-reduce:transition-none"
                  style={{ animation: 'rmFadeIn 240ms ease-out' }}
                >
                  <span className="w-5 font-mono text-xs tabular-nums text-ink-400">
                    #{i + 1}
                  </span>
                  <span className="text-lg leading-none" aria-hidden="true">
                    {slot.country?.flag_emoji ?? ''}
                  </span>
                  <span className="flex-1 truncate font-display text-base font-medium italic text-ink-900">
                    {name}
                  </span>
                  <span className="shrink-0 font-mono text-sm font-medium tabular-nums text-terra-600">
                    {percent}%
                  </span>
                </li>
              );
            })}
      </ul>

      <p className="mt-4 flex items-center justify-between gap-3">
        <span className="truncate font-mono text-[11px] tracking-wider text-ink-500">
          {t('home.hero.preview.caption', { preset: presetLabel })}
        </span>
        <Link
          to="/quiz"
          className="shrink-0 font-mono text-[11px] font-medium uppercase tracking-wider text-terra-600 hover:underline"
        >
          {t('home.hero.preview.tryYours')}
        </Link>
      </p>
    </div>
  );
}
